import type {
  IsBillingEnabledFn,
  TeamPaymentsViewModelFactory,
} from '@/features/billing'
import { type Team } from '@/features/iam/teams/Team'
import { TabController, createTabSpec } from '@/infra/TabController'
import { TeamDetailsViewModel } from '@/features/iam/teams/TeamDetailsViewModel'
import { type TeamStore } from '@/features/iam/teams/TeamStore'
import { type GetCurrentMemberFn } from '@/features/iam'
import { task } from 'mobx-task'
import { computed } from 'mobx'
import { TeamMembersViewModel } from '@/features/iam/teams/TeamMembersViewModel'
import { type TeamRoleStore } from '@/features/iam/teams/TeamRoleStore'
import { type TeamMemberStore } from '@/features/iam/teams/TeamMemberStore'
import { type InvitationStore } from '@/features/iam/invitations/InvitationStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

export class TeamViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly tabs = new TabController<Tab>(() => [
    this.teamMembersVm.canManageTeamMember &&
      createTabSpec<Tab>('Members', 'Members'),
    ...(this.shouldShowBilling && this.teamPaymentsVm.canManageTeamBilling
      ? [createTabSpec<Tab>('PaymentMethods', 'Payment Methods')]
      : []),
    createTabSpec<Tab>(
      'Details',
      'Details',
      () => !this.teamDetailsVm.validation.isValid,
    ),
  ])

  readonly teamDetailsVm: TeamDetailsViewModel
  readonly teamMembersVm: TeamMembersViewModel
  readonly teamPaymentsVm: ReturnType<TeamPaymentsViewModelFactory>

  constructor(
    public readonly team: Team,
    private readonly _teamStore: TeamStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _teamRoleStore: TeamRoleStore,
    private readonly _teamMembersStore: TeamMemberStore,
    private readonly _invitationStore: InvitationStore,
    private readonly _isBillingEnabled: IsBillingEnabledFn,
    private readonly _createTeamPaymentsViewModel: TeamPaymentsViewModelFactory,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this.teamDetailsVm = new TeamDetailsViewModel(
      this.team,
      this._teamStore,
      this._getCurrentMember,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this.teamMembersVm = new TeamMembersViewModel(
      this.team,
      this._teamRoleStore,
      this._teamMembersStore,
      this._invitationStore,
      this._getCurrentMember,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this.teamPaymentsVm = this._createTeamPaymentsViewModel(this.team)

    this._lifecycle
      .manage(this.teamDetailsVm)
      .manage(this.teamMembersVm)
      .manage(this.teamPaymentsVm)
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  @computed
  get shouldShowBilling() {
    return this._isBillingEnabled()
  }

  uploadPicture = task.resolved(async (file: File) => {
    if (!/\.(jpe?g|png)$/i.test(file.name)) {
      this._newAlert()
        .withState('error')
        .saying('We only support PNG, JPEG and JPG files for profile pictures.')
        .autoDismiss()
        .show()
      return
    }

    if (file.size > maxPictureSize) {
      this._newAlert()
        .withState('error')
        .saying('Max image size is 10mb.')
        .autoDismiss()
        .show()
      return
    }

    // The picture stays as it was when the upload fails.
    await this._teamStore
      .changeAvatar(this.team.id, file)
      .catch(this._createAlertErrorHandler('Changing picture failed'))
      .catch(() => undefined)
  })
}

const maxPictureSize = 1024 * 1024 * 10

/**
 * View Tab
 */
export type Tab = 'Details' | 'PaymentMethods' | 'Members'
